import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePatientRole } from '@/hooks/usePatientRole';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  Pill,
  Activity,
  Droplets,
  Utensils,
  MoreHorizontal,
  Clock,
  Camera,
  Trash2,
  Eye,
  Dumbbell,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import type { CarePlanTask, TaskType } from '@/types/care-plan';
import { cn } from '@/lib/utils';
import { useActiveShiftSync } from '@/hooks/useActiveShiftSync';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DAYS_KEYS = ['days.sun', 'days.mon', 'days.tue', 'days.wed', 'days.thu', 'days.fri', 'days.sat'];

export default function CarePlanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { canEditCarePlan: canEditRole, isLoading: roleLoading, isPatientActive, isPatientSuspended, isPatientArchived } = usePatientRole(patientId);
  const canEdit = canEditRole && isPatientActive;

  const { hasActiveShift, syncTask } = useActiveShiftSync(patientId);
  const [pendingSyncTaskId, setPendingSyncTaskId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const [tasks, setTasks] = useState<CarePlanTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [patientName, setPatientName] = useState('');

  useEffect(() => {
    if (patientId && user) {
      fetchPatient();
      fetchTasks();
    }
  }, [patientId, user]);

  const fetchPatient = async () => {
    if (!patientId) return;
    const { data } = await supabase
      .from('patients')
      .select('nickname')
      .eq('id', patientId)
      .single();
    if (data) setPatientName(data.nickname);
  };

  const fetchTasks = async () => {
    if (!patientId) return;

    try {
      const { data, error } = await supabase
        .from('care_plan_tasks')
        .select('*')
        .eq('patient_id', patientId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setTasks((data as unknown as CarePlanTask[]) || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTask = async (taskId: string) => {
    if (!canEdit) {
      toast.error(t('carePlan.noPermission'));
      return;
    }

    try {
      const { error } = await supabase
        .from('care_plan_tasks')
        .update({ is_active: false })
        .eq('id', taskId);

      if (error) throw error;
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success(t('carePlan.taskRemoved'));

      if (hasActiveShift) {
        setPendingSyncTaskId(taskId);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(t('carePlan.errorRemovingTask'));
    }
  };

  const handleConfirmSync = async () => {
    if (!pendingSyncTaskId) return;
    setIsSyncing(true);
    try {
      const result = await syncTask(pendingSyncTaskId);
      toast.success(
        result && (result.added || result.removed || result.updated)
          ? t('carePlan.activeShiftSyncDoneDetail', {
              added: result.added,
              removed: result.removed,
              updated: result.updated,
            })
          : t('carePlan.activeShiftSyncDone'),
      );
    } catch (error) {
      console.error('Error syncing active shifts:', error);
      toast.error(t('carePlan.activeShiftSyncError'));
    } finally {
      setIsSyncing(false);
      setPendingSyncTaskId(null);
    }
  };

  const formatScheduleDays = (days: number[] = []) => {
    return DAYS_KEYS.map((key, i) => (
      <span
        key={i}
        className={cn(
          "w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium",
          days.includes(i)
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground"
        )}
      >
        {t(key)}
      </span>
    ));
  };

  const formatTimes = (times: string[] = []) => {
    return times.map(time => time.slice(0, 5)).join(', ');
  };

  const getTaskTypeIcon = (taskType: TaskType) => {
    switch (taskType) {
      case 'fixed_medication':
      case 'sporadic_medication':
        return <Pill className="w-4 h-4" />;
      case 'vital_sign':
        return <Activity className="w-4 h-4" />;
      case 'hygiene':
        return <Droplets className="w-4 h-4" />;
      case 'food':
        return <Utensils className="w-4 h-4" />;
      case 'physical_activity':
        return <Dumbbell className="w-4 h-4" />;
      default:
        return <MoreHorizontal className="w-4 h-4" />;
    }
  };

  const getTaskTypeBadge = (task: CarePlanTask) => {
    if (task.task_type === 'physical_activity') {
      return { label: t('carePlan.physicalActivity'), variant: 'default' as const, className: 'bg-success/10 text-success border-success/20' };
    }
    switch (task.task_type) {
      case 'fixed_medication':
        return { label: t('carePlan.fixedMeds'), variant: 'default' as const, className: 'bg-primary/10 text-primary border-primary/20' };
      case 'sporadic_medication':
        return { label: t('carePlan.sporadicMeds'), variant: 'default' as const, className: 'bg-sporadic/10 text-sporadic border-sporadic/20' };
      case 'vital_sign':
        return { label: t('carePlan.vitals'), variant: 'default' as const, className: 'bg-info/10 text-info border-info/20' };
      case 'hygiene':
        return { label: t('carePlan.hygiene'), variant: 'default' as const, className: 'bg-primary/10 text-primary border-primary/20' };
      case 'food':
        return { label: t('carePlan.food'), variant: 'default' as const, className: 'bg-warning/10 text-warning border-warning/20' };
      default:
        return { label: t('carePlan.otherTasks'), variant: 'secondary' as const, className: '' };
    }
  };

  const getVitalTypeLabel = (type: string) => {
    switch (type) {
      case 'blood_pressure': return t('vitals.bloodPressure');
      case 'glucose': return t('vitals.glucose');
      case 'temperature': return t('vitals.temperature');
      case 'oxygen_saturation': return t('vitals.oxygenSaturation');
      case 'weight': return t('vitals.weight');
      case 'heart_rate': return t('vitals.heartRate');
      default: return type;
    }
  };

  const getFirstScheduleTime = (times: string[] = []): string | null => {
    if (!times || times.length === 0) return null;
    return times[0]?.slice(0, 5);
  };

  // Sort tasks by first schedule time
  const sortedTasks = [...tasks].sort((a, b) => {
    const timeA = getFirstScheduleTime(a.schedule_times) || '99:99';
    const timeB = getFirstScheduleTime(b.schedule_times) || '99:99';
    return timeA.localeCompare(timeB);
  });

  if (isLoading || roleLoading) {
    return (
      <AppLayout showFAB={false}>
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="w-10 h-10 rounded-full" />
          <Skeleton className="h-6 w-40" />
        </div>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 mb-3 rounded-lg" />
        ))}
      </AppLayout>
    );
  }

  const pageTitle = `${t('carePlan.title')} - ${patientName}`;

  return (
    <AppLayout
      title={pageTitle}
      showBack
      onBack={() => navigate(`/app/patients/${patientId}`)}
      showFAB={false}
    >
      <div className="p-4">
        {(isPatientSuspended || isPatientArchived) && (
          <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            {isPatientSuspended
              ? t('patient.suspended.writeBlocked', { defaultValue: 'Paciente suspenso. O plano de cuidados está em modo somente leitura até a assinatura ser reativada.' })
              : t('patient.archived.writeBlocked', { defaultValue: 'Paciente arquivado. O plano de cuidados está em modo somente leitura.' })}
          </div>
        )}
        {/* Top controls */}
        <div className="flex items-center justify-between mb-4">
          {!canEdit ? (
            <span className="text-xs flex items-center gap-1 text-muted-foreground bg-muted px-2 py-1 rounded-full">
              <Eye className="w-3 h-3" />
              {t('carePlan.viewOnly')}
            </span>
          ) : (
            <div />
          )}
          {canEdit && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/app/patients/${patientId}/care-plan/ai-wizard`)}
                size="sm"
                className="gap-1"
              >
                <Sparkles className="w-4 h-4" />
                {t('carePlan.aiWizard')}
              </Button>
              <Button
                onClick={() => navigate(`/app/patients/${patientId}/care-plan/add`)}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('carePlan.addTask')}
              </Button>
            </div>
          )}
        </div>

        {/* Empty state */}
        {sortedTasks.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground mb-4">{t('carePlan.noTasks')}</p>
              {canEdit && (
                <Button onClick={() => navigate(`/app/patients/${patientId}/care-plan/add`)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('carePlan.addFirstTask')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Task Cards - Sorted by time */}
        <div className="space-y-3">
          {sortedTasks.map((task) => {
            const badgeInfo = getTaskTypeBadge(task);
            const firstTime = getFirstScheduleTime(task.schedule_times);

            return (
              <Card
                key={task.id}
                className={cn(
                  "overflow-hidden",
                  canEdit && "cursor-pointer hover:bg-muted/50 transition-colors"
                )}
                onClick={() => canEdit && navigate(`/app/patients/${patientId}/care-plan/edit/${task.id}`)}
              >
                <CardContent className="p-0">
                  <div className="flex">
                    {/* Time column */}
                    <div className="w-20 flex-shrink-0 bg-muted/30 flex flex-col items-center justify-center p-3 border-r border-border">
                      {firstTime ? (
                        <>
                          <Clock className="w-4 h-4 text-muted-foreground mb-1" />
                          <span className="text-lg font-bold">{firstTime}</span>
                          {task.schedule_times && task.schedule_times.length > 1 && (
                            <span className="text-xs text-muted-foreground">
                              +{task.schedule_times.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t('carePlan.noTime')}</span>
                      )}
                    </div>

                    {/* Content column */}
                    <div className="flex-1 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {/* Badge and name */}
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className={cn("text-[12px]", badgeInfo.className)}>
                              {getTaskTypeIcon(task.task_type)}
                              <span className="ml-1">{badgeInfo.label}</span>
                            </Badge>
                            {task.photo_required && (
                              <Camera className="w-3 h-3 text-muted-foreground" />
                            )}
                          </div>

                          <h3 className="font-semibold text-sm">{task.name}</h3>

                          {/* Dose info */}
                          {task.dose && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {task.dose} {task.dose_unit}
                              {task.administration_route && ` - ${task.administration_route}`}
                            </p>
                          )}

                          {/* Sporadic info */}
                          {task.is_sporadic && (
                            <p className="text-xs text-sporadic mt-1">
                              {t('carePlan.minInterval')}: {task.min_interval_hours}h
                              {task.max_daily_doses && ` • ${t('carePlan.maxDailyDose')}: ${task.max_daily_doses}`}
                            </p>
                          )}

                          {/* Vital info */}
                          {task.vital_type && (
                            <div className="mt-1 space-y-0.5">
                              <p className="text-xs font-medium text-info">
                                {getVitalTypeLabel(task.vital_type)}
                              </p>
                            </div>
                          )}

                          {/* Schedule days - only show if not all days */}
                          {task.schedule_days && task.schedule_days.length > 0 && task.schedule_days.length < 7 && (
                            <div className="flex gap-0.5 mt-2">
                              {formatScheduleDays(task.schedule_days)}
                            </div>
                          )}

                          {/* Multiple times */}
                          {task.schedule_times && task.schedule_times.length > 1 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('carePlan.schedule')}: {formatTimes(task.schedule_times)}
                            </p>
                          )}
                        </div>

                        {/* Delete button */}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTask(task.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <AlertDialog open={!!pendingSyncTaskId} onOpenChange={(open) => { if (!open && !isSyncing) setPendingSyncTaskId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('carePlan.activeShiftSyncTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('carePlan.activeShiftSyncRemoveDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSyncing}>{t('carePlan.activeShiftSyncSkip')}</AlertDialogCancel>
            <AlertDialogAction disabled={isSyncing} onClick={(e) => { e.preventDefault(); handleConfirmSync(); }}>
              {t('carePlan.activeShiftSyncApply')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
